// Taken from https://tanstack.com/virtual/latest/docs/framework/react/examples/infinite-scroll
import { useVirtualizer } from '@tanstack/react-virtual';
import LoadingIndicator from 'gcs-common/components/LoadingIndicator/LoadingIndicator';
import { type ReactNode, useEffect, useRef } from 'react';

interface InfiniteScrollerProps<T> {
  fetchNextPage: VoidFunction;
  hasNextPage: boolean;
  rowRenderer: (item: T, index: number) => ReactNode;
  allRows: T[];
  isFetchingNextPage: boolean;
  isFetching: boolean;
  estimateSize: number;
  onEmpty?: () => ReactNode;
  listClassName?: string;
}

function InfiniteScroller<T>({
  fetchNextPage,
  hasNextPage,
  rowRenderer,
  allRows,
  isFetchingNextPage,
  isFetching,
  estimateSize,
  onEmpty,
  listClassName = '',
}: InfiniteScrollerProps<T>) {
  const parentRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: hasNextPage ? allRows.length + 1 : allRows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => estimateSize,
    overscan: 5,
  });

  useEffect(() => {
    const [lastItem] = [...rowVirtualizer.getVirtualItems()].reverse();

    if (!lastItem) {
      return;
    }

    if (lastItem.index >= allRows.length - 1 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage, allRows.length, isFetchingNextPage, rowVirtualizer.getVirtualItems()]);

  return (
    <div ref={parentRef} className={`List h-full w-full overflow-auto ${listClassName}`}>
      {/* For initial Loading state */}
      {isFetching && allRows.length === 0 && <LoadingIndicator />}
      {!isFetching && allRows.length === 0 && onEmpty ? (
        onEmpty()
      ) : (
        <div className="relative w-full" style={{ height: `${rowVirtualizer.getTotalSize()}px` }}>
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const isLoaderRow = virtualRow.index > allRows.length - 1;
            const item = allRows[virtualRow.index];

            return (
              <div
                key={virtualRow.index}
                className="absolute top-0 left-0 w-full items-center justify-center"
                style={{
                  height: `${virtualRow.size}px`,
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                {isLoaderRow ? (
                  hasNextPage ? (
                    <LoadingIndicator />
                  ) : undefined
                ) : (
                  item && rowRenderer(item, virtualRow.index)
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default InfiniteScroller;
